<script lang="ts">
  import { MessageSquare, Eye, CreditCard, X } from '@lucide/svelte';
  import { createMutation } from '@tanstack/svelte-query';

  interface Props {
    client: any;
    quotes: any[];
    quotesQueryIsLoading: boolean;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
    invalidateQuotes: () => void;
    invalidateOrders: () => void;
    setActiveTab: (tab: string) => void;
  }

  let { 
    client, 
    quotes = [], 
    quotesQueryIsLoading, 
    addToast, 
    invalidateQuotes, 
    invalidateOrders,
    setActiveTab
  }: Props = $props();

  let selectedQuote = $state<any>(null);

  // Mutation to convert approved quote to order
  const convertMutation = createMutation({
    mutationFn: (id: string) => client.convertQuoteToOrder(id),
    onSuccess: () => {
      invalidateQuotes();
      invalidateOrders();
      addToast('Quote successfully converted to order!', 'success');
      selectedQuote = null;
      // Switch tab to orders to show their new order
      setActiveTab('orders');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to convert quote to order.', 'error');
    }
  });

  function openQuote(quote: any) {
    selectedQuote = quote;
  }

  function closeQuote() {
    selectedQuote = null;
  }

  function handleConvert(id: string) {
    $convertMutation.mutate(id);
  }
</script>

<div class="space-y-6">
  <div class="space-y-1">
    <h2 class="text-3xl font-extrabold tracking-tight text-base-content">My Quotes</h2>
    <p class="text-sm text-base-content/70">Check the status of your customized amulets or large crystal orders</p>
  </div>

  {#if quotesQueryIsLoading}
    <div class="space-y-4">
      <div class="skeleton h-12 w-full"></div>
      <div class="skeleton h-16 w-full"></div>
      <div class="skeleton h-16 w-full"></div>
    </div>
  {:else if !quotes || quotes.length === 0}
    <div class="py-20 text-center border border-dashed border-base-300 rounded-3xl bg-base-100 flex flex-col items-center justify-center space-y-4">
      <div class="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center text-base-content/40">
        <MessageSquare class="w-6 h-6" />
      </div>
      <div>
        <p class="text-base-content/50 font-medium">No quote requests found.</p>
        <p class="text-xs text-base-content/40 mt-1">Submit custom quote requests from the product page to see them here.</p>
      </div>
    </div>
  {:else}
    <!-- Desktop Table View -->
    <div class="hidden md:block bg-base-100 border border-base-300 rounded-3xl overflow-hidden shadow-sm">
      <div class="overflow-x-auto">
        <table class="table table-zebra table-md w-full">
          <thead>
            <tr class="bg-base-200/50 text-xs font-semibold text-base-content/70 uppercase tracking-wider border-b border-base-300">
              <th>Quote ID</th>
              <th>Reference Name</th>
              <th>Submitted Date</th>
              <th>Total Value</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="text-base-content/95">
            {#each quotes as quote}
              <tr class="hover border-b border-base-200">
                <td class="font-mono text-xs font-bold text-accent">
                  #{quote.id.slice(-8).toUpperCase()}
                </td>
                <td class="text-xs font-semibold text-base-content/85">
                  {quote.name || 'Spiritual Inquiry'}
                </td>
                <td class="text-xs text-base-content/65">
                  {new Date(quote.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </td>
                <td class="font-bold text-base-content">
                  {#if quote.total}
                    <span class="text-xs font-semibold">Rs.</span> {Number(quote.total).toFixed(2)}
                  {:else}
                    <span class="text-xs text-base-content/40">Reviewing Price</span>
                  {/if}
                </td>
                <td>
                  <span class="badge badge-sm badge-outline font-bold uppercase tracking-wider {
                    quote.status === 'PENDING' ? 'badge-warning text-warning-content' :
                    quote.status === 'REVIEWED' ? 'badge-secondary text-secondary-content' :
                    quote.status === 'APPROVED' ? 'badge-success text-success-content' :
                    quote.status === 'REJECTED' ? 'badge-error text-error-content' :
                    'badge-neutral text-neutral-content'
                  }">
                    {quote.status}
                  </span>
                </td>
                <td class="text-right">
                  <div class="flex items-center justify-end gap-2 ml-auto">
                    {#if quote.status === 'APPROVED'}
                      <button 
                        onclick={() => handleConvert(quote.id)} 
                        disabled={$convertMutation.isPending}
                        class="btn btn-accent btn-xs rounded-xl font-bold flex items-center gap-1 cursor-pointer text-white h-7"
                      >
                        <CreditCard class="w-3.5 h-3.5" /> Pay Now
                      </button>
                    {/if}
                    <button 
                      onclick={() => openQuote(quote)} 
                      class="btn btn-ghost btn-xs text-accent font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <Eye class="w-3.5 h-3.5" /> Details
                    </button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div class="md:hidden space-y-4">
      {#each quotes as quote}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div 
          class="card bg-base-100 border border-base-300 rounded-2xl p-4 shadow-sm hover:border-primary/30 transition-all duration-300 space-y-3 cursor-pointer"
          onclick={() => openQuote(quote)}
        >
          <!-- Card Header (Quote ID & Status Badge) -->
          <div class="flex items-center justify-between">
            <span class="font-mono text-xs font-black text-accent">
              #{quote.id.slice(-8).toUpperCase()}
            </span>
            <span class="badge badge-xs badge-outline font-black uppercase tracking-wider {
              quote.status === 'PENDING' ? 'badge-warning text-warning-content' :
              quote.status === 'REVIEWED' ? 'badge-secondary text-secondary-content' :
              quote.status === 'APPROVED' ? 'badge-success text-success-content' :
              quote.status === 'REJECTED' ? 'badge-error text-error-content' :
              'badge-neutral text-neutral-content'
            }">
              {quote.status}
            </span>
          </div>

          <!-- Reference & Date -->
          <div class="grid grid-cols-2 gap-2 text-[11px] pt-1">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Reference Name</span>
              <span class="font-bold text-base-content block">{quote.name || 'Spiritual Inquiry'}</span>
            </div>
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Date Submitted</span>
              <span class="font-semibold text-base-content/75 block">
                {new Date(quote.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </span>
            </div>
          </div>

          <!-- Total Value & Trigger Action Buttons -->
          <div class="grid grid-cols-2 gap-2 pt-2.5 border-t border-base-200 text-[11px] items-center">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Total Value</span>
              <span class="font-black text-xs text-accent">
                {#if quote.total}
                  Rs. {Number(quote.total).toFixed(2)}
                {:else}
                  Reviewing Price
                {/if}
              </span>
            </div>
            <div class="flex items-center justify-end gap-2 ml-auto">
              {#if quote.status === 'APPROVED'}
                <button 
                  onclick={(e) => { e.stopPropagation(); handleConvert(quote.id); }} 
                  disabled={$convertMutation.isPending}
                  class="btn btn-accent btn-xs rounded-lg font-bold flex items-center gap-1 cursor-pointer text-white h-6 min-h-6 text-[9px] px-2"
                >
                  Pay Now
                </button>
              {/if}
              <button 
                onclick={(e) => { e.stopPropagation(); openQuote(quote); }} 
                class="btn btn-ghost btn-xs text-accent font-bold px-2 h-6 min-h-6 rounded-md text-[10px]"
              >
                Details
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Quote Details Modal -->
{#if selectedQuote}
  <dialog class="modal modal-open z-50">
    <div class="modal-box bg-base-100 border border-base-300 shadow-2xl rounded-3xl max-w-2xl w-full p-6 md:p-8 space-y-6 relative">
      
      <!-- Close Button -->
      <button 
        onclick={closeQuote} 
        class="btn btn-ghost btn-circle btn-sm text-base-content/60 hover:bg-base-200 absolute right-4 top-4 flex items-center justify-center cursor-pointer"
        aria-label="Close dialog"
      >
        <X class="w-5 h-5" />
      </button>

      <!-- Header Info -->
      <div class="border-b border-base-200 pb-4 space-y-1.5">
        <h3 class="font-black text-xl text-base-content flex items-center gap-2">
          Quote Request
          <span class="font-mono text-sm text-accent">#{selectedQuote.id.slice(-8).toUpperCase()}</span>
        </h3>
        <p class="text-xs text-base-content/50">
          Created on {new Date(selectedQuote.createdAt).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}
        </p>
      </div>

      <!-- Items List -->
      <div class="space-y-4">
        <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/65">Requested Items</h4>
        <div class="space-y-3 max-h-42 overflow-y-auto pr-2">
          {#each selectedQuote.items || [] as item}
            <div class="flex items-center justify-between p-3 bg-base-200/50 rounded-2xl border border-base-300/40">
              <div class="min-w-0 flex-1">
                <p class="text-sm font-bold text-base-content truncate">{item.product?.name || 'Custom Product'}</p>
                <p class="text-xs text-base-content/50 mt-0.5">Quantity: {item.quantity}</p>
              </div>
              <div class="text-sm font-extrabold text-base-content shrink-0 pl-4">
                {#if item.product?.offerPrice}
                  Rs. {(Number(item.product.offerPrice) * item.quantity).toFixed(2)}
                {:else}
                  Pending Price Review
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>

      <!-- Detail specifications -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-base-200">
        <div class="space-y-2">
          <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/65">Message & Requirements</h4>
          <p class="text-xs leading-relaxed text-base-content/80 font-medium whitespace-pre-line italic">
            "{selectedQuote.message || 'No additional specifications provided.'}"
          </p>
        </div>
        <div class="space-y-2">
          <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/65">Consultation Notes (Admin)</h4>
          <p class="text-xs leading-relaxed text-accent font-semibold">
            {selectedQuote.adminNotes || 'Under review by our spiritual guides.'}
          </p>
        </div>
      </div>

      <!-- Validity Info -->
      {#if selectedQuote.validUntil}
        <div class="text-[11px] text-warning/80 font-bold bg-warning/5 border border-warning/10 p-3 rounded-xl">
          This quote remains valid until {new Date(selectedQuote.validUntil).toLocaleDateString(undefined, { dateStyle: 'medium' })}.
        </div>
      {/if}

      <!-- Total and Conversions -->
      <div class="border-t border-base-200 pt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-base-200/30 p-4 rounded-2xl">
        <div>
          <span class="text-xs font-bold uppercase tracking-wider text-base-content/60">Total Value</span>
          <p class="text-xl font-black text-base-content">
            {#if selectedQuote.total}
              Rs. {Number(selectedQuote.total).toFixed(2)}
            {:else}
              Pending Calculations
            {/if}
          </p>
        </div>
        <div class="flex gap-2">
          {#if selectedQuote.status === 'APPROVED'}
            <button 
              onclick={() => handleConvert(selectedQuote.id)} 
              disabled={$convertMutation.isPending}
              class="btn btn-accent btn-sm rounded-xl font-bold text-white flex items-center gap-2 cursor-pointer h-10 px-4"
            >
              {#if $convertMutation.isPending}
                <span class="loading loading-spinner loading-xs"></span>
                Processing Order...
              {:else}
                <CreditCard class="w-4 h-4" /> Convert to Paid Order
              {/if}
            </button>
          {/if}
        </div>
      </div>

      <!-- Close dialog footer action -->
      <div class="modal-action mt-2">
        <button onclick={closeQuote} class="btn btn-outline btn-sm rounded-xl font-bold cursor-pointer">
          Close Details
        </button>
      </div>
    </div>
    <button class="modal-backdrop bg-black/60 cursor-default" onclick={closeQuote} aria-label="Close dialog"></button>
  </dialog>
{/if}
